// petconnect-v2/frontend/src/components/features/Feed/PostDetailPage.js
// Full-page post detail — navigated to via /post/:postId

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Heart, MessageCircle, MapPin, Send, Trash2,
  Share2, Check, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI';   // ← 3 levels up
import { useAuth } from '../../../hooks/useAuth'; // ← 3 levels up

// ── Image carousel ────────────────────────────────────────────────────────────
const ImageCarousel = ({ urls }) => {
  const [idx, setIdx] = useState(0);

  if (!urls || urls.length === 0) {
    return (
      <div className="w-full aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center rounded-2xl">
        <span className="text-7xl">🐾</span>
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-gray-900 group" style={{ maxHeight: '520px' }}>
      <img
        src={urls[idx]}
        alt={`Media ${idx + 1}`}
        className="w-full h-full object-contain"
        style={{ maxHeight: '520px', minHeight: '220px' }}
        onError={e => { e.target.style.display = 'none'; }}
      />

      {urls.length > 1 && (
        <>
          <button
            onClick={() => setIdx((idx - 1 + urls.length) % urls.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2.5 opacity-0 group-hover:opacity-100 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => setIdx((idx + 1) % urls.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2.5 opacity-0 group-hover:opacity-100 transition-all"
          >
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {urls.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/80'}`}
              />
            ))}
          </div>
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-bold px-2.5 py-1 rounded-full">
            {idx + 1} / {urls.length}
          </div>
        </>
      )}
    </div>
  );
};

// ── Category badge ────────────────────────────────────────────────────────────
const CategoryBadge = ({ category }) => {
  const map = {
    found:           { label: '🟢 FOUND',    cls: 'bg-green-500' },
    missing:         { label: '🔴 MISSING',  cls: 'bg-red-500' },
    adoption:        { label: '❤️ ADOPTION', cls: 'bg-pink-500' },
    success_stories: { label: '⭐ SUCCESS',  cls: 'bg-yellow-500' },
    general:         { label: '📍 GENERAL',  cls: 'bg-blue-500' },
  };
  const { label, cls } = map[category] || map.general;
  return (
    <span className={`inline-block ${cls} text-white text-xs font-bold px-3 py-1 rounded-full`}>
      {label}
    </span>
  );
};

// ── Share button ──────────────────────────────────────────────────────────────
const ShareButton = ({ postId }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/post/${postId}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'PetConnectAI Post', url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleShare}
      className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition font-medium text-sm"
    >
      {copied ? <Check size={16} className="text-green-500" /> : <Share2 size={16} />}
      {copied ? 'Copied!' : 'Share'}
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PostDetailPage = () => {
  const { postId }   = useParams();
  const navigate     = useNavigate();
  const { apiCall }  = useAPI();
  const { user, isAnonymous } = useAuth();

  const [post, setPost]               = useState(null);
  const [comments, setComments]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [liked, setLiked]             = useState(false);
  const [likesCount, setLikesCount]   = useState(0);
  const [newComment, setNewComment]   = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [notFound, setNotFound]       = useState(false);

  // Fetch post from the feed (no single-post endpoint available)
  const loadPost = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall(`/posts?category=feed&page_size=200`);
      const found = (data.posts || []).find(p => String(p.id) === String(postId));
      if (found) {
        if (!Array.isArray(found.media_urls)) {
          found.media_urls = found.media_url ? [found.media_url] : [];
        }
        setPost(found);
        setLiked(found.liked || false);
        setLikesCount(found.likes_count || 0);
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [apiCall, postId]);

  const loadComments = useCallback(async () => {
    try {
      const data = await apiCall(`/posts/${postId}/comments`);
      setComments(Array.isArray(data) ? data : []);
    } catch {
      setComments([]);
    }
  }, [apiCall, postId]);

  useEffect(() => {
    loadPost();
    loadComments();
    document.title = 'Post | PetConnectAI';
  }, [loadPost, loadComments]);

  useEffect(() => {
    if (post) {
      document.title = post.pet_name
        ? `${post.pet_name} — PetConnectAI`
        : `Post — PetConnectAI`;
    }
  }, [post]);

  const handleLike = async () => {
    try {
      if (liked) {
        await apiCall(`/posts/${post.id}/unlike`, { method: 'DELETE' });
        setLiked(false);
        setLikesCount(c => c - 1);
      } else {
        await apiCall(`/posts/${post.id}/like`, { method: 'POST' });
        setLiked(true);
        setLikesCount(c => c + 1);
      }
    } catch {}
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await apiCall(`/posts/${post.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content: newComment }),
      });
      setNewComment('');
      await loadComments();
    } catch (err) {
      alert(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await apiCall(`/posts/${post.id}/comments/${commentId}`, { method: 'DELETE' });
      setComments(cs => cs.filter(c => c.id !== commentId));
    } catch {}
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await apiCall(`/posts/${post.id}`, { method: 'DELETE' });
      navigate(-1);
    } catch (err) {
      alert(err.message || 'Failed to delete post');
    }
  };

  const handleMarkResolved = async () => {
    const newStatus = post.status === 'resolved' ? 'active' : 'resolved';
    try {
      await apiCall(`/posts/${post.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      setPost(p => ({ ...p, status: newStatus }));
    } catch (err) {
      alert(err.message);
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading post…</p>
        </div>
      </div>
    );
  }

  // ── Not found ──
  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <div className="text-6xl mb-4">🐾</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Post not found</h2>
          <p className="text-gray-500 mb-6">This post may have been removed or doesn't exist.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition"
          >
            Back to Feed
          </button>
        </div>
      </div>
    );
  }

  const isOwner = user && !isAnonymous && post.user_id === user.id;
  const adoption_extras = post.adoption_extras || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top nav */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Back</span>
          </button>
          <Link to="/" className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            🐾 PetConnectAI
          </Link>
          <ShareButton postId={post.id} />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Media */}
        {post.media_urls?.length > 0 && <ImageCarousel urls={post.media_urls} />}

        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

          {/* Author */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0">
                {post.author?.profile_picture ? (
                  <img src={post.author.profile_picture} alt={post.author.username} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                    {post.author?.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
              </div>
              <div>
                <p className="font-bold text-gray-900">{post.author?.username || 'Unknown'}</p>
                <p className="text-xs text-gray-400">
                  {new Date(post.created_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
            <CategoryBadge category={post.category} />
          </div>

          {/* Resolved banner */}
          {post.status === 'resolved' && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="font-bold text-green-800">Pet Reunited with Owner!</p>
                <p className="text-xs text-green-600">This case has been resolved.</p>
              </div>
            </div>
          )}

          {/* Pet info */}
          {(post.pet_name || post.pet_type) && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
              <p className="font-black text-blue-900 text-lg mb-1">
                🐾 {post.pet_name || 'Unknown Pet'}
              </p>
              <div className="flex flex-wrap gap-2 text-sm text-blue-700">
                {post.pet_type && <span className="capitalize">Type: <strong>{post.pet_type}</strong></span>}
                {post.breed && post.pet_type === 'dog' && (
                  <><span>·</span><span>Breed: <strong>{post.breed}</strong></span></>
                )}
                {post.sex && post.sex !== 'unknown' && (
                  <><span>·</span><span className="capitalize">Sex: <strong>{post.sex}</strong></span></>
                )}
                {post.missing_since && (
                  <><span>·</span><span>
                    {post.category === 'missing' ? 'Missing since' : 'Found on'}:{' '}
                    <strong>{new Date(post.missing_since).toLocaleDateString()}</strong>
                  </span></>
                )}
              </div>
            </div>
          )}

          {/* Adoption extras */}
          {post.category === 'adoption' && (
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: 'vaccinated',     icon: '💉', label: 'Vaccinated' },
                { key: 'neutered',       icon: '✂️', label: 'Neutered' },
                { key: 'microchipped',   icon: '📡', label: 'Microchipped' },
                { key: 'good_with_kids', icon: '👶', label: 'Good with kids' },
                { key: 'good_with_pets', icon: '🐾', label: 'Good with pets' },
                { key: 'house_trained',  icon: '🏠', label: 'House trained' },
              ].map(({ key, icon, label }) => {
                const val = post[key] ?? adoption_extras[key];
                if (val === null || val === undefined) return null;
                return (
                  <span key={key} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                    val ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                  }`}>
                    {icon} {val ? label : `Not ${label}`}
                  </span>
                );
              })}
              {(post.size || adoption_extras.size) && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-800">
                  📏 {post.size || adoption_extras.size}
                </span>
              )}
              {(post.color || adoption_extras.color) && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                  🎨 {post.color || adoption_extras.color}
                </span>
              )}
            </div>
          )}

          {/* Content */}
          <p className="text-gray-800 text-base leading-relaxed whitespace-pre-wrap mb-4">{post.content}</p>

          {/* Owner message */}
          {post.owner_message && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs font-bold text-amber-700 mb-1">💬 Message from poster</p>
              <p className="text-sm text-amber-900">{post.owner_message}</p>
            </div>
          )}

          {/* Special needs */}
          {(post.special_needs || adoption_extras.special_needs) && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs font-bold text-rose-700 mb-1">⚠️ Special needs</p>
              <p className="text-sm text-rose-900">{post.special_needs || adoption_extras.special_needs}</p>
            </div>
          )}

          {/* Location */}
          {post.location_name && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <MapPin size={15} className="text-gray-400 flex-shrink-0" />
              <span>{post.location_name}</span>
            </div>
          )}

          {/* Contact */}
          {post.contact_info && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs font-bold text-green-700 mb-0.5">📞 Contact</p>
              <p className="text-base font-semibold text-green-900">{post.contact_info}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100 flex-wrap">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition ${
                liked
                  ? 'bg-red-50 text-red-500 border border-red-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-red-200 hover:text-red-400'
              }`}
            >
              <Heart size={17} fill={liked ? 'currentColor' : 'none'} />
              {likesCount}
            </button>

            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-50 text-gray-600 border border-gray-200 text-sm">
              <MessageCircle size={17} />
              {comments.length}
            </div>

            {isOwner && post.category === 'missing' && (
              <button
                onClick={handleMarkResolved}
                className={`ml-auto px-4 py-2.5 rounded-xl text-sm font-semibold border transition ${
                  post.status === 'resolved'
                    ? 'border-orange-200 text-orange-600 hover:bg-orange-50'
                    : 'border-green-200 text-green-600 hover:bg-green-50'
                }`}
              >
                {post.status === 'resolved' ? '↩ Undo resolved' : '✓ Mark reunited'}
              </button>
            )}

            {isOwner && (
              <button
                onClick={handleDeletePost}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-red-200 text-red-500 hover:bg-red-50 transition"
              >
                <Trash2 size={15} />
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-bold text-gray-900 mb-4 text-lg">
            Comments
            {comments.length > 0 && (
              <span className="text-gray-400 font-normal text-base ml-1">({comments.length})</span>
            )}
          </h3>

          {/* Comment input */}
          {!isAnonymous ? (
            <form onSubmit={handleComment} className="flex gap-3 mb-6">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {user?.username?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Add a comment…"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-400 outline-none text-sm"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submitting}
                  className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </div>
            </form>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-800">
              <Link to="/login" className="font-semibold underline">Sign in</Link> to leave a comment.
            </div>
          )}

          {/* Comment list */}
          {comments.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <MessageCircle size={36} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No comments yet. Be the first!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {comments.map(comment => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-9 h-9 rounded-full bg-purple-400 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {comment.author?.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-semibold text-sm text-gray-900">{comment.author?.username}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </p>
                        {user && comment.user_id === user.id && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-gray-300 hover:text-red-400 transition"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostDetailPage;